import React, {
  useMemo,
  useState,
} from 'react';
import Button from 'components/common/button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullableCommunique } from 'types/types';
import styles from 'components/podiatry/page.module.scss';
import clsx from 'clsx';
import { AppointmentOptions } from 'components/appointmentConfirmation';
import Spinner from 'components/spinner/Spinner';
import values from 'lodash/values';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import { communiqueAction } from 'reduxStateSlice/communique';
import { useRouter } from 'next/router';
import {
  getLinkQueryParams,
  isEmptyString,
  parseErrorMessage,
} from 'utils/helper';
import omit from 'lodash/omit';
import { useGetAppointmentCommuniqueByToken, useSetAppointmentConfirmation } from 'hooks/useApi';

type Props = {
  isCancel: boolean;
  selectedStatus?: AppointmentOptions;
  doNotCancel?: (arg: boolean) => void;
  onAppointmentChangeError: (error: unknown) => void;
}

function Confirmation({
  isCancel,
  selectedStatus,
  doNotCancel,
  onAppointmentChangeError,
} : Props) {
  const router = useRouter();
  const { token } = getLinkQueryParams(router.query);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    refetch: refetchAppointmentCommunique,
    isFetching: isFetchingAppointmentCommunique,
  } = useGetAppointmentCommuniqueByToken({ token: token ?? '' }, false);
  const { mutateAsync: setAppointmentConfirmation } = useSetAppointmentConfirmation();
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const onSubmit = async () => {
    const payload = {
      appointmentId: communiqueData?.appointmentId ?? 0,
      confirmationStatus: selectedStatus?.value ?? '',
    };
    setIsLoading(true);
    try {
      await setAppointmentConfirmation(payload);
      setIsCancelled(true);
    } catch (error: unknown) {
      onAppointmentChangeError(error);
    } finally {
      setIsLoading(false);
    }
  };
  const hasFormEnabled = useMemo(() => {
    const requiredForms = omit(communiqueData?.sendForms, 'dosespotConsentForm');
    return values(requiredForms).some(
      (form) => form,
    );
  }, [communiqueData?.sendForms]);

  const intakeForms = async () => {
    setErrorMessage('');
    setIsLoading(true);
    try {
      const { data } = await refetchAppointmentCommunique();
      if (!data) throw new Error('No content found with this link.');
      dispatch(communiqueAction(data));
      dispatch(
        appStateSettingsAction({ name: ModuleTypes.disclaimerMessage, page: null }),
      );
    } catch (e) {
      setErrorMessage(parseErrorMessage(e) as string);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmationMessage = useMemo(() => {
    const isSelectConfirm = /Confirmed/i.test(selectedStatus?.type ?? '');
    const isSelectCancelled = /Cancelled/i.test(selectedStatus?.type ?? '');
    if (isSelectConfirm || isSelectCancelled) {
      return `Your Appointment has been ${selectedStatus?.type}!`;
    }
    return 'Your request to reschedule has been submitted successfully. Our office will reach out to you shortly to confirm the new appointment time. Thank you!';
  }, [selectedStatus?.type]);

  return (
    <div className="text-center">
      {isCancel && !isCancelled ? (
        <>
          <h2 className={clsx(styles.titleText, '!mb-4')}>Your Appointment will be canceled</h2>
          {!isEmptyString(errorMessage) && <p className="error-message">{errorMessage}</p>}
          {!isLoading ? (
            <>
              <Button
                className={clsx('!bg-red-600 !text-white !hover:bg-red-700 !w-1/2 !m-auto !my-2 !disabled:text-white')}
                variant="outlined"
                onClick={onSubmit}
                disabled={isLoading}
              >
                Confirm cancellation
              </Button>
              <Button
                onClick={() => { doNotCancel?.(false); }}
                className="!w-1/2 !m-auto"
                disabled={isLoading}
                variant="outlined"
              >
                Do not cancel
              </Button>
            </>
          ) : (
            <div className="flex flex-auto justify-center items-center text-xs">
              <Spinner />
              Loading...
            </div>
          )}
        </>
      ) : (
        <div className={clsx(styles.titleText, 'flex flex-col gap-2 justify-center items-center h-screen')}>
          <h2>
            {confirmationMessage}
          </h2>
          {!isEmptyString(errorMessage) && <p className="error-message">{errorMessage}</p>}
          {hasFormEnabled && selectedStatus?.type === 'Confirmed' && (
            <div className="flex flex-col gap-4 my-4">
              <p>Let&apos;s complete Intake forms</p>
              <Button
                onClick={intakeForms}
                className="mt-2"
                disabled={isLoading || isFetchingAppointmentCommunique}
              >
                Continue
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Confirmation;
